import { Button } from "@mui/material"
import { trackMenuClicks } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { Desktop, Mobile } from "common/MediaQueries"
import { HeaderTopButtonsLinks } from "common/SiteLinks"
import { Font } from "components/shopFeatures/wizard/constants"
import React from "react"
import styled from "styled-components"
import colors from "theme/colors"

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-inline-start: auto;
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    gap: 12px;
    justify-content: flex-end;
    width: fit-content;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-inline-start: 0;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
    padding: 15px 20px;
  }
`

// Primary CTA — red fill (רכישה Online)
const PrimaryButton = styled(Button)`
  background-color: ${(p) => p.theme.colors.newPrimaryRed};
  color: ${colors.white};
  border: 2px solid ${(p) => p.theme.colors.newPrimaryRed};
  border-radius: 3px;
  padding: 16px 24px;
  white-space: nowrap;
  text-transform: none;
  max-height: 54px;
  // Mixed-script label ("רכישה Online"): MazdaType is unicode-range-scoped to
  // Latin (U+0000–017F), so Latin glyphs render in MazdaType and Hebrew falls
  // through to OENaPro per-glyph. (Font.MazdaType === "MazdaType, OENaPro, …")
  font-family: ${Font.MazdaType};
  letter-spacing: 0;
  &:hover {
    box-shadow: none;
    background-color: ${(p) => p.theme.colors.newPrimaryRed};
    border: 2px solid ${(p) => p.theme.colors.newPrimaryRed};
    color: ${colors.white};
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding: 7px 10px;
    min-width: auto;
  }
`

// Secondary CTA — outlined white (הזמנת נסיעת הדגמה)
const OutlinedSecondaryButton = styled(Button)`
  &.outlined-secondary {
    min-width: 120px;
    font-size: 16px;
    width: fit-content;
    color: #ffffff;
    // Per-script label (e.g. "רכישה Online"): MazdaType is unicode-range-scoped
    // to Latin, so Latin renders in MazdaType and Hebrew falls back to OENaPro.
    // Used for both CTAs incl. the mobile-drawer primary. (Pure-Hebrew labels
    // are unaffected since every glyph falls back to OENaPro.)
    font-family: ${Font.MazdaType};
    background-color: transparent;
    border: 2px solid #ffffff;
    padding: 16px 24px;
    text-transform: none;
    white-space: nowrap;
    max-height: 54px;
    letter-spacing: 0;
    &:hover {
      box-shadow: none;
      background-color: #d5d5d5 !important;
      color: #101010;
      border: 2px solid #d5d5d5;
    }
    ${(p) => p.theme.breakpoints.down("sm")} {
      padding: 0 11px;
      height: 32px;
    }
  }
`

type Props = {
  closeDrawer?: (event: React.KeyboardEvent | React.MouseEvent) => void
  setSubLinks?: any
}

// CTA order (DLK-553): primary red "רכישה Online" then outlined "הזמנת נסיעת הדגמה".
// In RTL the first child sits on the right, matching the Figma (red on the right).
const PRIMARY_CTA = HeaderTopButtonsLinks[1]
const SECONDARY_CTA = HeaderTopButtonsLinks[0]

const HeaderTopButtons: React.FC<Props> = ({ closeDrawer, setSubLinks }) => {
  const handleClick = (e: React.MouseEvent) => {
    setSubLinks?.(null)
    trackMenuClicks()
    closeDrawer?.(e)
  }

  return (
    <StyledWrapper>
      <Desktop>
        <NextLink href={PRIMARY_CTA.link} openInNewTab={PRIMARY_CTA.blank}>
          <PrimaryButton
            onClick={handleClick}
            tabIndex={-1}
            role="presentation"
          >
            {PRIMARY_CTA.title}
          </PrimaryButton>
        </NextLink>
      </Desktop>
      <NextLink href={SECONDARY_CTA.link} openInNewTab={SECONDARY_CTA.blank}>
        <OutlinedSecondaryButton
          variant="outlined"
          className="outlined-secondary"
          onClick={handleClick}
          tabIndex={-1}
          role="presentation"
        >
          {SECONDARY_CTA.title}
        </OutlinedSecondaryButton>
      </NextLink>
      <Mobile>
        <NextLink href={PRIMARY_CTA.link} openInNewTab={PRIMARY_CTA.blank}>
          <OutlinedSecondaryButton
            variant="outlined"
            className="outlined-secondary"
            onClick={handleClick}
            tabIndex={-1}
            role="presentation"
          >
            {PRIMARY_CTA.title}
          </OutlinedSecondaryButton>
        </NextLink>
      </Mobile>
    </StyledWrapper>
  )
}

export default HeaderTopButtons
